import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';

const Fallback = ({ message }: { message?: string }) => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
          <AlertTriangle className="h-6 w-6 text-amber-500" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
            {message ?? t('This referential could not be displayed.')}
          </p>
          <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
            {t('The rest of the administration still works. Reload the page; if it persists, the incident has been reported.')}
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t('Reload')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Boundary around one admin screen. The app already has a root boundary, but it swaps the whole
 * interface for an error page: a bad row in one screen should cost that screen, not the sidebar
 * and every other admin page. Sentry still receives the exception.
 *
 * `message` names what failed — the technician record is not a referential, and a fallback that
 * says otherwise sends the reader looking in the wrong place.
 */
const ReferentialBoundary = ({ children, message }: { children: ReactNode; message?: string }) => (
  <Sentry.ErrorBoundary fallback={<Fallback message={message} />}>{children}</Sentry.ErrorBoundary>
);

export default ReferentialBoundary;
